import { useState } from 'react'
import {
  useCreditCosts,
  useUpdateCreditCost,
  useCreateCreditCost,
  useSimulateCostChange,
} from '@/hooks/useCredits'
import { DataTable } from '@/components/common/DataTable'
import { StatusBadge } from '@/components/common/StatusBadge'
import { FilterDropdown } from '@/components/common/FilterDropdown'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CreditCost, SimulateCostChangeResponse } from '@/types'
import { Plus, Pencil, Calculator, Zap } from 'lucide-react'

const categoryOptions = [
  { value: 'all', label: 'Todas' },
  { value: 'scraping', label: 'Scraping' },
  { value: 'analysis', label: 'Analysis' },
  { value: 'ai', label: 'AI' },
  { value: 'reporting', label: 'Reporting' },
  { value: 'alerts', label: 'Alerts' },
]

export const CreditCostsPage = () => {
  const { data: costs, isLoading } = useCreditCosts()
  const updateCost = useUpdateCreditCost()
  const createCost = useCreateCreditCost()
  const simulateCost = useSimulateCostChange()

  const [categoryFilter, setCategoryFilter] = useState('all')
  const [editCost, setEditCost] = useState<CreditCost | null>(null)
  const [editCredits, setEditCredits] = useState(0)
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState({
    action_type: '',
    platform: '',
    category: 'scraping',
    credits: 0,
    description: '',
  })
  const [simulation, setSimulation] = useState<SimulateCostChangeResponse | null>(null)

  const filteredCosts = costs?.filter(
    (c) => categoryFilter === 'all' || c.category === categoryFilter
  )

  const openEdit = (cost: CreditCost) => {
    setEditCost(cost)
    setEditCredits(cost.credits)
    setSimulation(null)
  }

  const handleSimulate = async () => {
    if (!editCost) return
    const result = await simulateCost.mutateAsync({
      action_type: editCost.action_type,
      platform: editCost.platform,
      new_credits: editCredits,
    })
    setSimulation(result)
  }

  const handleSave = async () => {
    if (!editCost) return
    await updateCost.mutateAsync({
      id: editCost.id,
      updates: { credits: editCredits },
    })
    setEditCost(null)
    setSimulation(null)
  }

  const handleCreate = async () => {
    await createCost.mutateAsync(createForm)
    setCreateOpen(false)
    setCreateForm({
      action_type: '',
      platform: '',
      category: 'scraping',
      credits: 0,
      description: '',
    })
  }

  const columns = [
    {
      key: 'action',
      header: 'Accion',
      cell: (c: CreditCost) => (
        <div>
          <p className="font-mono text-sm font-medium">{c.action_type}</p>
          <p className="text-xs text-muted-foreground">{c.description}</p>
        </div>
      ),
    },
    {
      key: 'platform',
      header: 'Plataforma',
      cell: (c: CreditCost) => <span className="capitalize">{c.platform}</span>,
    },
    {
      key: 'category',
      header: 'Categoria',
      cell: (c: CreditCost) => (
        <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs capitalize">
          <Zap className="h-3 w-3" />
          {c.category}
        </span>
      ),
    },
    {
      key: 'credits',
      header: 'Creditos',
      cell: (c: CreditCost) => <span className="text-lg font-bold">{c.credits}</span>,
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (c: CreditCost) => <StatusBadge status={c.is_active ? 'active' : 'inactive'} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (c: CreditCost) => (
        <div className="flex items-center justify-end gap-2">
          <Switch
            checked={c.is_active}
            onCheckedChange={(v) => updateCost.mutate({ id: c.id, updates: { is_active: v } })}
          />
          <Button variant="ghost" size="sm" onClick={() => openEdit(c)}>
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Costos de Creditos</h1>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo costo
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <FilterDropdown
          label="Categoria"
          options={categoryOptions}
          value={categoryFilter}
          onChange={setCategoryFilter}
        />
        {costs && (
          <span className="ml-auto text-sm text-muted-foreground">
            {costs.length} acciones configuradas
          </span>
        )}
      </div>

      <DataTable
        columns={columns}
        data={filteredCosts ?? []}
        isLoading={isLoading}
        keyExtractor={(c) => c.id}
        emptyMessage="No hay costos de creditos configurados"
      />

      {/* Edit Dialog with Simulation */}
      <Dialog open={!!editCost} onOpenChange={(o) => !o && setEditCost(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Editar: {editCost?.action_type} ({editCost?.platform})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label>Creditos</Label>
              <Input
                type="number"
                min={0}
                value={editCredits}
                onChange={(e) => setEditCredits(Number(e.target.value))}
              />
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={handleSimulate}
              disabled={simulateCost.isPending || editCredits === editCost?.credits}
            >
              <Calculator className="mr-2 h-4 w-4" />
              {simulateCost.isPending ? 'Simulando...' : 'Simular impacto'}
            </Button>

            {simulation && (
              <Card className="bg-muted/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Resultado de simulacion</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <p>
                    Cambio: {simulation.current_credits} → {simulation.new_credits}{' '}
                    <span
                      className={
                        simulation.change_percentage > 0 ? 'text-destructive' : 'text-green-600'
                      }
                    >
                      ({simulation.change_percentage > 0 ? '+' : ''}
                      {simulation.change_percentage}%)
                    </span>
                  </p>
                  <p>Usuarios afectados: {simulation.estimated_impact_users}</p>
                  <p>
                    Impacto en creditos:{' '}
                    <span className="font-medium">
                      {simulation.estimated_impact_credits > 0 ? '+' : ''}
                      {simulation.estimated_impact_credits.toLocaleString()}
                    </span>{' '}
                    en {simulation.period_days} dias
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCost(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={updateCost.isPending}>
              {updateCost.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo costo de credito</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Tipo de accion</Label>
              <Input
                placeholder="scrape_posts"
                value={createForm.action_type}
                onChange={(e) => setCreateForm((f) => ({ ...f, action_type: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Plataforma</Label>
              <Input
                placeholder="twitter, instagram, any..."
                value={createForm.platform}
                onChange={(e) => setCreateForm((f) => ({ ...f, platform: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Categoria</Label>
                <Select
                  value={createForm.category}
                  onValueChange={(v) => setCreateForm((f) => ({ ...f, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scraping">Scraping</SelectItem>
                    <SelectItem value="analysis">Analysis</SelectItem>
                    <SelectItem value="ai">AI</SelectItem>
                    <SelectItem value="reporting">Reporting</SelectItem>
                    <SelectItem value="alerts">Alerts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Creditos</Label>
                <Input
                  type="number"
                  min={0}
                  value={createForm.credits}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, credits: Number(e.target.value) }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Descripcion</Label>
              <Input
                placeholder="Descripcion de la accion..."
                value={createForm.description}
                onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createCost.isPending || !createForm.action_type || !createForm.platform}
            >
              {createCost.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
